import React, { useState, useEffect } from 'react';
import http from '../../api/http';
import { Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { timeAgo } from '../../components/layouts/mainLayout/HeaderRight';
import { getReports, createReport, getReportById, deleteReport, exportReport } from '../../api/smart-life';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

interface Report {
  _id: string;
  userId: string;
  userInfo: {
    age: number;
    gender: string;
    race: string;
  };
  biomarkersCount?: number;
  interpretationsCount?: number;
  createdAt: string;
  updatedAt?: string;
  __v?: number;
  metadata: {
    source: string;
    apiVersion?: string;
    processingTimeMs?: number;
  };
  inputData?: {
    biomarkers: Array<{
      nameEn: string;
      labReferenceRange: { min: number; max: number };
      standardizedValue: number;
    }>;
    timestamp?: string;
  };
  outputData?: {
    interpretations: Array<{
      _id: number | string;
      name: {
        en: string;
        de?: string;
      };
      recommendations: Array<{
        _id: number | string;
        name: {
          en: string;
          de?: string;
        };
        perc: number;
      }>;
    }>;
    timestamp?: string;
    algorithmVersion: string;
  };
}

interface ReportsResponse {
  success: boolean;
  data: Report[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

const ReportsPage = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    currentPage: 1
  });
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formData, setFormData] = useState<{
    userId: string;
    userInfo: {
      age: number;
      gender: string;
      race: string;
    };
    inputData: {
      biomarkers: Array<{
        nameEn: string;
        labReferenceRange: { min: number; max: number };
        standardizedValue: number;
      }>;
    };
    outputData: {
      interpretations: Array<{
        _id: number | string;
        name: {
          en: string;
          de?: string;
        };
        recommendations: Array<{
          _id: number | string;
          name: {
            en: string;
            de?: string;
          };
          perc: number;
        }>;
      }>;
      algorithmVersion: string;
    };
    metadata: {
      source: string;
      processingTimeMs: number;
    };
  }>({
    userId: '',
    userInfo: {
      age: 0,
      gender: '',
      race: ''
    },
    inputData: {
      biomarkers: []
    },
    outputData: {
      interpretations: [],
      algorithmVersion: ''
    },
    metadata: {
      source: '',
      processingTimeMs: 0
    }
  });

  useEffect(() => {
    fetchReports();
  }, [pagination.currentPage, pagination.limit, filters.userId, filters.startDate, filters.endDate]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const offset = (pagination.currentPage - 1) * pagination.limit;
      const params: any = {
        limit: pagination.limit,
        offset: offset + 1
      };
      
      if (filters.userId) params.userId = filters.userId;
      if (filters.startDate) {
        const date = new Date(filters.startDate);
        params.startDate = date.toISOString();
      }
      if (filters.endDate) {
        const date = new Date(filters.endDate);
        params.endDate = date.toISOString();
      }

      const data: ReportsResponse = await getReports(params);
      
      if (data.success && data.data) {
        setReports(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          offset: data.pagination?.offset || 0
        }));
      } else {
        setReports([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      setReports([]);
      if (error.response?.data?.message) {
        alert(`Error: ${error.response.data.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const resetForm = () => {
    setFormData({
      userId: '',
      userInfo: {
        age: 0,
        gender: '',
        race: ''
      },
      inputData: {
        biomarkers: []
      },
      outputData: {
        interpretations: [],
        algorithmVersion: ''
      },
      metadata: {
        source: '',
        processingTimeMs: 0
      }
    });
    setCreateError(null);
  };

  const handleAddBiomarker = () => {
    setFormData(prev => ({
      ...prev,
      inputData: {
        biomarkers: [
          ...prev.inputData.biomarkers,
          {
            nameEn: '',
            labReferenceRange: { min: 0, max: 0 },
            standardizedValue: 0
          }
        ]
      }
    }));
  };

  const handleRemoveBiomarker = (index: number) => {
    setFormData(prev => ({
      ...prev,
      inputData: {
        biomarkers: prev.inputData.biomarkers.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUpdateBiomarker = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const updated = [...prev.inputData.biomarkers];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[index] = {
          ...updated[index],
          [parent]: {
            ...(updated[index] as any)[parent],
            [child]: value
          }
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return {
        ...prev,
        inputData: {
          biomarkers: updated
        }
      };
    });
  };

  const handleAddInterpretation = () => {
    setFormData(prev => ({
      ...prev,
      outputData: {
        ...prev.outputData,
        interpretations: [
          ...prev.outputData.interpretations,
          {
            _id: '',
            name: { en: '', de: '' },
            recommendations: []
          }
        ]
      }
    }));
  };

  const handleRemoveInterpretation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      outputData: {
        ...prev.outputData,
        interpretations: prev.outputData.interpretations.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUpdateInterpretation = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const updated = [...prev.outputData.interpretations];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[index] = {
          ...updated[index],
          [parent]: {
            ...(updated[index] as any)[parent],
            [child]: value
          }
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return {
        ...prev,
        outputData: {
          ...prev.outputData,
          interpretations: updated
        }
      };
    });
  };

  const handleAddRecommendation = (interpretationIndex: number) => {
    setFormData(prev => {
      const updated = [...prev.outputData.interpretations];
      updated[interpretationIndex] = {
        ...updated[interpretationIndex],
        recommendations: [
          ...updated[interpretationIndex].recommendations,
          {
            _id: '',
            name: { en: '', de: '' },
            perc: 0
          }
        ]
      };
      return {
        ...prev,
        outputData: {
          ...prev.outputData,
          interpretations: updated
        }
      };
    });
  };

  const handleRemoveRecommendation = (interpretationIndex: number, recommendationIndex: number) => {
    setFormData(prev => {
      const updated = [...prev.outputData.interpretations];
      updated[interpretationIndex] = {
        ...updated[interpretationIndex],
        recommendations: updated[interpretationIndex].recommendations.filter((_, i) => i !== recommendationIndex)
      };
      return {
        ...prev,
        outputData: {
          ...prev.outputData,
          interpretations: updated
        }
      };
    });
  };

  const handleUpdateRecommendation = (interpretationIndex: number, recommendationIndex: number, field: string, value: any) => {
    setFormData(prev => {
      const updated = [...prev.outputData.interpretations];
      const recommendations = [...updated[interpretationIndex].recommendations];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        recommendations[recommendationIndex] = {
          ...recommendations[recommendationIndex],
          [parent]: {
            ...(recommendations[recommendationIndex] as any)[parent],
            [child]: value
          }
        };
      } else {
        recommendations[recommendationIndex] = {
          ...recommendations[recommendationIndex],
          [field]: value
        };
      }
      updated[interpretationIndex] = {
        ...updated[interpretationIndex],
        recommendations
      };
      return {
        ...prev,
        outputData: {
          ...prev.outputData,
          interpretations: updated
        }
      };
    });
  };

  const handleViewDetails = async (reportId: string) => {
    setDetailLoading(true);
    setIsDetailModalOpen(true);
    try {
      const response = await getReportById(reportId);
      if (response.success && response.data) {
        setSelectedReport(response.data);
      } else {
        setSelectedReport(null);
        alert('Failed to load report details');
      }
    } catch (error: any) {
      console.error('Failed to fetch report details:', error);
      setSelectedReport(null);
      alert(error.response?.data?.message || 'Failed to load report details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      const response = await deleteReport(reportId);
      if (response.success) {
        alert('Report deleted successfully');
        fetchReports();
        if (selectedReport && selectedReport._id === reportId) {
          setIsDetailModalOpen(false);
          setSelectedReport(null);
        }
      } else {
        alert(response.message || 'Failed to delete report');
      }
    } catch (error: any) {
      console.error('Failed to delete report:', error);
      alert(error.response?.data?.message || 'Failed to delete report');
    }
  };

  const handleExportReport = async (reportId: string, format: 'json' | 'xlsx') => {
    try {
      const response = await exportReport(reportId, format);
      
      if (format === 'xlsx') {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `report-${reportId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const dataStr = JSON.stringify(response, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `report-${reportId}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      console.error('Failed to export report:', error);
      alert(error.response?.data?.message || 'Failed to export report');
    }
  };

  const handleCreateReport = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const reportPayload = {
        userId: formData.userId,
        userInfo: formData.userInfo,
        inputData: {
          biomarkers: formData.inputData.biomarkers.map(b => ({
            ...b,
            labReferenceRange: {
              min: Number(b.labReferenceRange.min),
              max: Number(b.labReferenceRange.max)
            },
            standardizedValue: Number(b.standardizedValue)
          }))
        },
        outputData: {
          interpretations: formData.outputData.interpretations.map(i => ({
            ...i,
            _id: Number(i._id) || i._id,
            recommendations: i.recommendations.map(r => ({
              ...r,
              _id: Number(r._id) || r._id,
              perc: Number(r.perc)
            }))
          })),
          algorithmVersion: formData.outputData.algorithmVersion
        },
        metadata: {
          source: formData.metadata.source,
          processingTimeMs: Number(formData.metadata.processingTimeMs)
        }
      };

      const response = await createReport(reportPayload);
      
      if (response.success) {
        setIsCreateModalOpen(false);
        resetForm();
        fetchReports();
        alert('Report created successfully!');
      } else {
        setCreateError(response.message || 'Failed to create report');
      }
    } catch (error: any) {
      console.error('Failed to create report:', error);
      setCreateError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        'Failed to create report. Please check the form data.'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Reports
        </Typography>
        <Button
          variant="contained"
          onClick={() => setIsCreateModalOpen(true)}
          startIcon={<AddIcon />}
        >
          Create Report
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Statistics
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Reports</Typography>
            <Typography variant="h6">{pagination.total}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Current Page</Typography>
            <Typography variant="h6">{pagination.currentPage} / {totalPages || 1}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Reports per Page</Typography>
            <Typography variant="h6">{pagination.limit}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Showing</Typography>
            <Typography variant="h6">{reports.length} reports</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 2 }}>
          <TextField
            label="User ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            size="small"
            placeholder="Filter by user ID"
          />
          <TextField
            label="Start Date"
            type="datetime-local"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="datetime-local"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Race</TableCell>
              <TableCell>Biomarkers Count</TableCell>
              <TableCell>Interpretations Count</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : reports.length > 0 ? (
              reports.map((report) => (
                <TableRow key={report._id}>
                  <TableCell>{report._id || '-'}</TableCell>
                  <TableCell>{report.userId || '-'}</TableCell>
                  <TableCell>{report.userInfo?.age !== undefined ? report.userInfo.age : '-'}</TableCell>
                  <TableCell>{report.userInfo?.gender || '-'}</TableCell>
                  <TableCell>{report.userInfo?.race || '-'}</TableCell>
                  <TableCell>{report.biomarkersCount !== undefined ? report.biomarkersCount : (report.inputData?.biomarkers?.length || 0)}</TableCell>
                  <TableCell>{report.interpretationsCount !== undefined ? report.interpretationsCount : (report.outputData?.interpretations?.length || 0)}</TableCell>
                  <TableCell>{report.createdAt ? timeAgo(report.createdAt) : '-'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewDetails(report._id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No reports found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>
              Page {pagination.currentPage} of {totalPages} ({pagination.total} total reports)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={pagination.currentPage <= 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={pagination.currentPage >= totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Dialog
        open={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Report</Typography>
            <IconButton onClick={() => {
              setIsCreateModalOpen(false);
              resetForm();
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>User Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <TextField
                  label="User ID"
                  value={formData.userId}
                  onChange={(e) => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Age"
                  type="number"
                  value={formData.userInfo.age}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    userInfo: { ...prev.userInfo, age: Number(e.target.value) }
                  }))}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.userInfo.gender}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      userInfo: { ...prev.userInfo, gender: e.target.value }
                    }))}
                    label="Gender"
                  >
                    <MenuItem value="m">Male</MenuItem>
                    <MenuItem value="w">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Race"
                  value={formData.userInfo.race}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    userInfo: { ...prev.userInfo, race: e.target.value }
                  }))}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">Biomarkers</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddBiomarker}
                >
                  Add Biomarker
                </Button>
              </Box>
              {formData.inputData.biomarkers.map((biomarker, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Biomarker {index + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveBiomarker(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                    <TextField
                      label="Name (EN)"
                      value={biomarker.nameEn}
                      onChange={(e) => handleUpdateBiomarker(index, 'nameEn', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Min Range"
                      type="number"
                      value={biomarker.labReferenceRange.min}
                      onChange={(e) => handleUpdateBiomarker(index, 'labReferenceRange.min', Number(e.target.value))}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Max Range"
                      type="number"
                      value={biomarker.labReferenceRange.max}
                      onChange={(e) => handleUpdateBiomarker(index, 'labReferenceRange.max', Number(e.target.value))}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Standardized Value"
                      type="number"
                      value={biomarker.standardizedValue}
                      onChange={(e) => handleUpdateBiomarker(index, 'standardizedValue', Number(e.target.value))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Paper>
              ))}
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">Interpretations</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddInterpretation}
                >
                  Add Interpretation
                </Button>
              </Box>
              {formData.outputData.interpretations.map((interpretation, iIndex) => (
                <Paper key={iIndex} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Interpretation {iIndex + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveInterpretation(iIndex)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
                    <TextField
                      label="ID"
                      value={interpretation._id}
                      onChange={(e) => handleUpdateInterpretation(iIndex, '_id', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Name (EN)"
                      value={interpretation.name.en}
                      onChange={(e) => handleUpdateInterpretation(iIndex, 'name.en', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Name (DE)"
                      value={interpretation.name.de || ''}
                      onChange={(e) => handleUpdateInterpretation(iIndex, 'name.de', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">Recommendations</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddRecommendation(iIndex)}
                      >
                        Add Recommendation
                      </Button>
                    </Box>
                    {interpretation.recommendations.map((recommendation, rIndex) => (
                      <Paper key={rIndex} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption">Recommendation {rIndex + 1}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRecommendation(iIndex, rIndex)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
                          <TextField
                            label="ID"
                            value={recommendation._id}
                            onChange={(e) => handleUpdateRecommendation(iIndex, rIndex, '_id', e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Name (EN)"
                            value={recommendation.name.en}
                            onChange={(e) => handleUpdateRecommendation(iIndex, rIndex, 'name.en', e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Name (DE)"
                            value={recommendation.name.de || ''}
                            onChange={(e) => handleUpdateRecommendation(iIndex, rIndex, 'name.de', e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Percentage"
                            type="number"
                            value={recommendation.perc}
                            onChange={(e) => handleUpdateRecommendation(iIndex, rIndex, 'perc', Number(e.target.value))}
                            size="small"
                            fullWidth
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              ))}
              <TextField
                label="Algorithm Version"
                value={formData.outputData.algorithmVersion}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  outputData: { ...prev.outputData, algorithmVersion: e.target.value }
                }))}
                fullWidth
                sx={{ mt: 2 }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Metadata</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <TextField
                  label="Source"
                  value={formData.metadata.source}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, source: e.target.value }
                  }))}
                  fullWidth
                />
                <TextField
                  label="Processing Time (ms)"
                  type="number"
                  value={formData.metadata.processingTimeMs}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, processingTimeMs: Number(e.target.value) }
                  }))}
                  fullWidth
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsCreateModalOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateReport}
            variant="contained"
            disabled={creating || !formData.userId}
          >
            {creating ? 'Creating...' : 'Create Report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedReport(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Report Details</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedReport && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleExportReport(selectedReport._id, 'json')}
                  >
                    Export JSON
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleExportReport(selectedReport._id, 'xlsx')}
                  >
                    Export XLSX
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeleteReport(selectedReport._id)}
                  >
                    Delete
                  </Button>
                </>
              )}
              <IconButton onClick={() => {
                setIsDetailModalOpen(false);
                setSelectedReport(null);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : selectedReport ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Report ID</Typography>
                  <Typography variant="body1">{selectedReport._id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                  <Typography variant="body1">{selectedReport.userId}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                  <Typography variant="body1">{selectedReport.userInfo?.age !== undefined ? selectedReport.userInfo.age : '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedReport.userInfo?.gender || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Race</Typography>
                  <Typography variant="body1">{selectedReport.userInfo?.race || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Biomarkers Count</Typography>
                  <Typography variant="body1">{selectedReport.biomarkersCount !== undefined ? selectedReport.biomarkersCount : (selectedReport.inputData?.biomarkers?.length || 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Interpretations Count</Typography>
                  <Typography variant="body1">{selectedReport.interpretationsCount !== undefined ? selectedReport.interpretationsCount : (selectedReport.outputData?.interpretations?.length || 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1">{selectedReport.createdAt ? timeAgo(selectedReport.createdAt) : '-'}</Typography>
                </Box>
                {selectedReport.updatedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                    <Typography variant="body1">{timeAgo(selectedReport.updatedAt)}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                  <Typography variant="body1">{selectedReport.metadata?.source || '-'}</Typography>
                </Box>
                {selectedReport.metadata?.apiVersion && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">API Version</Typography>
                    <Typography variant="body1">{selectedReport.metadata.apiVersion}</Typography>
                  </Box>
                )}
                {selectedReport.metadata?.processingTimeMs !== undefined && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Processing Time (ms)</Typography>
                    <Typography variant="body1">{selectedReport.metadata.processingTimeMs}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Algorithm Version</Typography>
                  <Typography variant="body1">{selectedReport.outputData?.algorithmVersion || '-'}</Typography>
                </Box>
              </Box>

              {selectedReport.inputData?.biomarkers && selectedReport.inputData.biomarkers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Biomarkers</Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name (EN)</TableCell>
                          <TableCell>Min Range</TableCell>
                          <TableCell>Max Range</TableCell>
                          <TableCell>Standardized Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedReport.inputData.biomarkers.map((biomarker, index) => (
                          <TableRow key={index}>
                            <TableCell>{biomarker.nameEn}</TableCell>
                            <TableCell>{biomarker.labReferenceRange?.min}</TableCell>
                            <TableCell>{biomarker.labReferenceRange?.max}</TableCell>
                            <TableCell>{biomarker.standardizedValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {selectedReport.outputData?.interpretations && selectedReport.outputData.interpretations.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Interpretations</Typography>
                  {selectedReport.outputData.interpretations.map((interpretation, iIndex) => (
                    <Paper key={iIndex} sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Interpretation {iIndex + 1}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">ID: {interpretation._id}</Typography>
                        <Typography variant="body2" color="text.secondary">Name (EN): {interpretation.name?.en}</Typography>
                        {interpretation.name?.de && (
                          <Typography variant="body2" color="text.secondary">Name (DE): {interpretation.name.de}</Typography>
                        )}
                      </Box>
                      {interpretation.recommendations && interpretation.recommendations.length > 0 && (
                        <Box>
                          <Typography variant="body2" gutterBottom>Recommendations:</Typography>
                          <TableContainer component={Paper} sx={{ maxHeight: 200, overflow: 'auto' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Name (EN)</TableCell>
                                  <TableCell>Name (DE)</TableCell>
                                  <TableCell>Percentage</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {interpretation.recommendations.map((recommendation, rIndex) => (
                                  <TableRow key={rIndex}>
                                    <TableCell>{recommendation._id}</TableCell>
                                    <TableCell>{recommendation.name?.en}</TableCell>
                                    <TableCell>{recommendation.name?.de || '-'}</TableCell>
                                    <TableCell>{recommendation.perc}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Paper>
                  ))}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Algorithm Version: {selectedReport.outputData.algorithmVersion || '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography color="error">Failed to load report details</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsDetailModalOpen(false);
            setSelectedReport(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;

